import { useState } from 'react'
import { MOVEMENT_COPY } from '../../copy/movement'
import { fill } from '../../copy/firstRun'
import type { ExerciseCardModel } from './sessionModel'
import { TrendBadge } from './TrendBadge'
import { SetGrid, MetricGrid } from './SetGrid'
import { MuscleFigure } from '../muscle/MuscleFigure'
import './sessionDetail.css'

interface ExerciseCardProps {
  model: ExerciseCardModel
  delayMs?: number
}

/**
 * The platform an imported card's data came from. Only an import has one, so a
 * screen reader hears that ("Imported from Lanista") where the eye reads the
 * bare name; iOS speaks the same words (`ImportedSource.spokenLabel`).
 */
function SourceChip({ source }: { source: NonNullable<ExerciseCardModel['source']> }) {
  return (
    <span className="src">
      <span className="sr-only">Imported from </span>
      {source}
    </span>
  )
}

/**
 * The single card used for cardio, free-weight, and Milon exercises alike
 * (prototype: docs/plans/assets/2026-07-23-session-detail-prototype.html,
 * `.ex` / `cardEl()`). Media column (image + source chip + muscle-silhouette
 * overlay) on the left, name/trend/set-or-metric-grid body on the right.
 */
export function ExerciseCard({ model, delayMs = 0 }: ExerciseCardProps) {
  const [imageFailed, setImageFailed] = useState(false)
  const hasMuscles = model.primary.length > 0 || model.secondary.length > 0
  const isContain = model.imageFit === 'contain'

  const figs = hasMuscles ? (
    <div className="ex-figs">
      <MuscleFigure side="front" primary={model.primary} secondary={model.secondary} />
      <MuscleFigure side="back" primary={model.primary} secondary={model.secondary} />
    </div>
  ) : null

  return (
    <div className="ex reveal" style={{ '--d': `${delayMs}ms` } as React.CSSProperties}>
      {model.imageUrl && !imageFailed ? (
        <div className={isContain ? 'ex-media contain' : 'ex-media'}>
          {model.source && <SourceChip source={model.source} />}
          <img
            src={model.imageUrl}
            alt={model.name}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
          {figs}
        </div>
      ) : (
        <div className="ex-media placeholder">
          {model.source && <SourceChip source={model.source} />}
          <div>
            <div className="ph-name">{model.name}</div>
            <div className="ph-sub">No photo</div>
          </div>
          {figs}
        </div>
      )}

      <div className="ex-body">
        <div className="ex-head">
          <div>
            <div className="ex-name">{model.name}</div>
            {model.standsInFor && (
              <div className="ex-standin">
                {fill(MOVEMENT_COPY.swapStandsInFor, { name: model.standsInFor })}
              </div>
            )}
          </div>
          {model.trend && <TrendBadge verdict={model.trend.verdict} label={model.trend.label} />}
        </div>
        {model.body.kind === 'sets' ? (
          <SetGrid rows={model.body.rows} idxLabel={model.body.idxLabel} />
        ) : (
          <MetricGrid items={model.body.items} />
        )}
      </div>
    </div>
  )
}
